import { Box, Stack } from "@mui/material";
import { HighLevelKpiCard } from "./HighLevelKpiCard";

interface TargetVsActualItem {
  Proportional_Target: number;
  Actual: number;
  Target: number;
  Comment: string;
  Metrics: string;
  RR: number;
  Proportional_Attainment: number;
}

interface HighLevelKpisProps {
  data: TargetVsActualItem[];
}

const HIGH_LEVEL_METRICS = ["ftd", "profit", "nrpu"];

const METRIC_LABELS: Record<string, string> = {
  ftd: "FTD",
  profit: "Profit",
  nrpu: "NRPU",
};

const IS_CURRENCY_METRIC: Record<string, boolean> = {
  ftd: false,
  profit: true,
  nrpu: true,
};



function formatNumber(value: number, isCurrency: boolean): string {
  const rounded = Math.round(value);
  const formatted = rounded.toLocaleString();
  return isCurrency ? `$${formatted}` : formatted;
}

function getRrColor(attainment: number): "success" | "warning" | "error" {
  if (attainment >= 0.95) return "success"; // Green → >= 95%
  if (attainment >= 0.8) return "warning"; // Yellow → 80% to 95%
  return "error"; // Red → < 80%
}

export default function HighLevelKpis({ data }: HighLevelKpisProps) {
  const highLevelItems = HIGH_LEVEL_METRICS.map((metricKey) =>
    data?.find((item) => item.Metrics?.toLowerCase() === metricKey),
  ).filter(Boolean) as TargetVsActualItem[];

  if (!highLevelItems.length) {
    return null;
  }

  return (
    <Box>
      <Stack spacing={2}>
        {highLevelItems.map((item) => {
          const metricKey = item.Metrics?.toLowerCase();
          const isCurrency = IS_CURRENCY_METRIC[metricKey] ?? false;
          const attainment = item.Proportional_Attainment ?? 0;
          const progress = Math.min(Math.round(attainment * 100), 100);
          const rrPercent = `${(attainment * 100).toFixed(1)}%`;

          return (
            <HighLevelKpiCard
              key={item.Metrics}
              title={METRIC_LABELS[metricKey] || item.Metrics}
              value={formatNumber(item.Actual, isCurrency)}
              total={formatNumber(item.Target, isCurrency)}
              rr={rrPercent}
              rrColor={getRrColor(attainment)}
              progress={progress}
              predicted={formatNumber(item.RR, isCurrency)}
              extraInfo={item.Comment || undefined}
            />
          );
        })}
      </Stack>
    </Box>
  );
}
